import LoggerFactory from 'utilities/loggerv2/loggerFactory';
import ErrorCodes from 'utilities/loggerv2/errorCodes';
import { BasketAddItemOptions, BasketAmendItemOptions, BasketResponse } from 'types/BasketTypes';
import { formatError } from 'utilities/util';
import { Logger } from 'utilities/loggerv2/logger';
import { DEFAULT_BASKET_ERROR_MESSAGE } from 'constants/basket';
import { BasketServiceInterface } from './interface/BasketServiceInterface';
import { startSpan, endSpan, failSpan } from '../../utilities/loggerv2/performanceSpans';

export default class BasketService {
  private basketService: BasketServiceInterface;

  private loggerPromise: Promise<Logger>;

  constructor(basketService: BasketServiceInterface) {
    this.basketService = basketService;
    this.loggerPromise = LoggerFactory.getLogger('Basket service');
  }

  async addItem(options: BasketAddItemOptions): Promise<string> {
    return this.handleBasketAction(this.basketService.addItem.bind(this.basketService), options, 'addToBasket');
  }

  async amendItem(options: BasketAmendItemOptions): Promise<string> {
    return this.handleBasketAction(
      this.basketService.amendItem.bind(this.basketService),
      options,
      'amendBasket',
      () => {
        window.location.href = '/basket';
      },
    );
  }

  private async handleBasketAction<T extends BasketAddItemOptions | BasketAmendItemOptions>(
    action: (options: T) => Promise<BasketResponse>,
    options: T,
    spanName: string,
    onSuccess?: () => void,
  ): Promise<string> {
    const spanId = startSpan(spanName, {
      productSku: options.item.variantId,
      hasPersonalisation: !!options.item.personalisation,
      hasWarranty: !!options.item.warrantyOptionNumber,
      hasAddons: !!(options.item.selectedAddOnServices && options.item.selectedAddOnServices.length > 0),
    });

    try {
      const response = await action(options);

      if (!response.success) {
        const errorMessage = `Status ${response.status ?? 'Unknown'}: ${response.statusText ?? 'Unknown'}`;
        const errorObj = new Error(errorMessage);

        Object.defineProperty(errorObj, 'code', {
          value: response.status ? response.status.toString() : 'UNKNOWN_HTTP_ERROR',
          writable: true,
          enumerable: true,
        });

        (await this.loggerPromise).logWithCode(ErrorCodes.BASKET_SERVICE_ERROR, errorObj);

        failSpan(spanId, {
          errorType: ErrorCodes.BASKET_SERVICE_ERROR.code,
          message: response.message,
          status: response.status,
          statusText: response.statusText || 'Unknown',
        });

        return response.message;
      }

      endSpan(spanId, { success: true });

      if (onSuccess) onSuccess();

      return response.message;
    } catch (error) {
      const errorObj = formatError(error, 'UNKNOWN_ERROR');

      (await this.loggerPromise).logWithCode(ErrorCodes.BASKET_SERVICE_ERROR, errorObj);

      failSpan(spanId, {
        errorType: ErrorCodes.BASKET_SERVICE_ERROR.code,
        message: errorObj.message || 'Unknown error',
        stack: errorObj.stack || '',
        errorCode: (errorObj as any).code || 'UNKNOWN_ERROR',
      });

      return DEFAULT_BASKET_ERROR_MESSAGE;
    }
  }
}
